import React, { useState, useMemo, useEffect } from 'react';
import { Check, Search, X, ChevronLeft } from 'lucide-react';
import { cn } from '../../../utils/cn';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AIAssessmentChat from './AIAssessmentChat';

const CategorySelect = ({ 
  label, 
  description, 
  value, 
  onChange, 
  onOptionSelect, 
  error, 
  required = false,
  placeholder = "Select a category" 
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAIAssessment, setShowAIAssessment] = useState(false);

  // Auto-open: lock body scroll on mount since overlay starts open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const categories = [
    {
      id: 'home-property',
      name: 'Home Services',
      icon: 'Home',
      color: '#E0528A',
      gradient: 'linear-gradient(135deg, #E0528A 0%, #F58A54 100%)',
      subcategories: [
        { id: 'roofing', name: 'Roofing', icon: 'Triangle' },
        { id: 'hvac', name: 'HVAC Installation/Replacement', icon: 'Wind' },
        { id: 'plumbing', name: 'Plumbing Projects', icon: 'Droplets' },
        { id: 'electrical', name: 'Electrical Work', icon: 'Zap' },
        { id: 'painting', name: 'Painting', icon: 'Paintbrush' },
        { id: 'flooring', name: 'Flooring', icon: 'Grid2X2' },
        { id: 'windows-doors', name: 'Windows & Doors', icon: 'DoorOpen' },
        { id: 'fencing', name: 'Fencing', icon: 'Fence' },
        { id: 'landscaping', name: 'Landscaping', icon: 'Leaf' },
        { id: 'pest-control', name: 'Pest Control', icon: 'Bug' },
        { id: 'pool-installation', name: 'Pool Installation/Repair', icon: 'Waves' },
        { id: 'solar-panels', name: 'Solar Panel Installation', icon: 'Sun' },
        { id: 'generators', name: 'Generators', icon: 'Bolt' },
        { id: 'crawl-space', name: 'Crawl Space', icon: 'ArrowDownToLine' },
        { id: 'other-home-property', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'travel-experiences',
      name: 'Travel & Experiences',
      icon: 'Plane',
      color: '#F06F6B',
      gradient: 'linear-gradient(135deg, #F06F6B 0%, #E0528A 100%)',
      subcategories: [
        { id: 'flights', name: 'Flights', icon: 'Plane' },
        { id: 'hotels', name: 'Hotels', icon: 'Hotel' },
        { id: 'vacation-packages', name: 'Vacation Packages', icon: 'Palmtree' },
        { id: 'cruises', name: 'Cruises', icon: 'Ship' },
        { id: 'rental-cars', name: 'Rental Cars', icon: 'Car' },
        { id: 'concerts', name: 'Concerts', icon: 'Music' },
        { id: 'sporting-events', name: 'Sporting Events', icon: 'Trophy' },
        { id: 'other-travel-experiences', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'vehicle-transportation',
      name: 'Vehicle & Auto',
      icon: 'Car',
      color: '#F06F6B',
      gradient: 'linear-gradient(135deg, #F06F6B 0%, #F58A54 100%)',
      subcategories: [
        { id: 'car-purchase', name: 'Car Purchase', icon: 'Car' },
        { id: 'motorcycle', name: 'Motorcycle Purchase', icon: 'Bike' },
        { id: 'rv', name: 'RV Purchase', icon: 'Caravan' },
        { id: 'boat', name: 'Boat Purchase', icon: 'Sailboat' },
        { id: 'car-insurance', name: 'Car Insurance', icon: 'ShieldCheck' },
        { id: 'auto-repair', name: 'Auto Repair', icon: 'Wrench' },
        { id: 'tires', name: 'Tires', icon: 'CircleDot' },
        { id: 'detailing', name: 'Detailing', icon: 'Sparkles' },
        { id: 'body-shop', name: 'Body Shop Work', icon: 'Hammer' },
        { id: 'other-vehicle-transportation', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'tech-electronics',
      name: 'Tech & Electronics',
      icon: 'Laptop',
      color: '#F58A54',
      gradient: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 100%)',
      subcategories: [
        { id: 'computers-laptops', name: 'Computers / Laptops', icon: 'Laptop' },
        { id: 'tablets', name: 'Tablets', icon: 'Tablet' },
        { id: 'smartphones', name: 'Smartphones', icon: 'Smartphone' },
        { id: 'tv-home-theater', name: 'TVs & Home Theater', icon: 'Tv' },
        { id: 'smart-home-devices', name: 'Smart Home Devices', icon: 'Home' },
        { id: 'audio-equipment', name: 'Audio Equipment / Speakers', icon: 'Speaker' },
        { id: 'headphones', name: 'Headphones', icon: 'Headphones' },
        { id: 'wearables', name: 'Wearables', icon: 'Watch' },
        { id: 'other-tech-electronics', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'financial-professional',
      name: 'Finance',
      icon: 'CreditCard',
      color: '#E0528A',
      gradient: 'linear-gradient(135deg, #E0528A 0%, #F58A54 100%)',
      subcategories: [
        { id: 'credit-cards', name: 'Credit Cards', icon: 'CreditCard' },
        { id: 'home-purchase', name: 'Home Purchase', icon: 'House' },
        { id: 'home-refinance', name: 'Home Refinance', icon: 'RefreshCw' },
        { id: 'accountants', name: 'Accountants / CPAs', icon: 'Calculator' },
        { id: 'attorneys', name: 'Attorneys', icon: 'Scale' },
        { id: 'financial-advisors', name: 'Financial Advisors', icon: 'TrendingUp' },
        { id: 'moving-companies', name: 'Moving Companies', icon: 'Truck' },
        { id: 'tutors', name: 'Tutors / Coaches', icon: 'UserCheck' },
        { id: 'other-financial-professional', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'life-events',
      name: 'Life Events',
      icon: 'Heart',
      color: '#E0528A',
      gradient: 'linear-gradient(135deg, #E0528A 0%, #F06F6B 100%)',
      subcategories: [
        { id: 'weddings', name: 'Weddings', icon: 'Heart' },
        { id: 'engagement-rings', name: 'Engagement Rings / Jewelry', icon: 'Gem' },
        { id: 'birthdays', name: 'Birthdays & Milestone Events', icon: 'PartyPopper' },
        { id: 'funerals', name: 'Funerals', icon: 'Flower2' },
        { id: 'childcare', name: 'Childcare / Daycare', icon: 'Baby' },
        { id: 'baby-products', name: 'Baby', icon: 'Baby' },
        { id: 'private-school', name: 'Private School Tuition', icon: 'School' },
        { id: 'other-life-events', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'insurance',
      name: 'Insurance',
      icon: 'Shield',
      color: '#F58A54',
      gradient: 'linear-gradient(135deg, #F58A54 0%, #E0528A 100%)',
      subcategories: [
        { id: 'auto-insurance', name: 'Auto Insurance', icon: 'Car' },
        { id: 'homeowners-insurance', name: 'Homeowners Insurance', icon: 'House' },
        { id: 'renters-insurance', name: 'Renters Insurance', icon: 'KeyRound' },
        { id: 'life-insurance', name: 'Life Insurance', icon: 'HeartHandshake' },
        { id: 'health-insurance', name: 'Health Insurance', icon: 'Stethoscope' },
        { id: 'pet-insurance', name: 'Pet Insurance', icon: 'PawPrint' },
        { id: 'business-insurance', name: 'Business Insurance', icon: 'Building2' },
        { id: 'other-insurance', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'home-purchases',
      name: 'Home Goods',
      icon: 'Sofa',
      color: '#F58A54',
      gradient: 'linear-gradient(135deg, #F58A54 0%, #E0528A 100%)',
      subcategories: [
        { id: 'major-appliances', name: 'Major Appliances', icon: 'WashingMachine' },
        { id: 'furniture', name: 'Furniture', icon: 'Sofa' },
        { id: 'mattresses', name: 'Mattresses', icon: 'BedDouble' },
        { id: 'home-gym', name: 'Home Gym Equipment', icon: 'Dumbbell' },
        { id: 'home-security', name: 'Home Security Systems', icon: 'ShieldCheck' },
        { id: 'lighting', name: 'Lighting', icon: 'Lightbulb' },
        { id: 'rugs', name: 'Rugs', icon: 'LayoutGrid' },
        { id: 'decor', name: 'Decor', icon: 'Sparkles' },
        { id: 'other-home-purchases', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'education',
      name: 'Education',
      icon: 'GraduationCap',
      color: '#E0528A',
      gradient: 'linear-gradient(135deg, #E0528A 0%, #F58A54 100%)',
      subcategories: [
        { id: 'college', name: 'College', icon: 'GraduationCap' },
        { id: 'student-loans', name: 'Student Loans', icon: 'Banknote' },
        { id: 'bootcamps', name: 'Bootcamps', icon: 'Code' },
        { id: 'certifications', name: 'Certifications', icon: 'Award' },
        { id: 'online-learning', name: 'Online Learning Programs', icon: 'MonitorPlay' },
        { id: 'professional-training', name: 'Professional Training', icon: 'BriefcaseBusiness' },
        { id: 'tutoring', name: 'Tutoring', icon: 'BookOpenCheck' },
        { id: 'other-education', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'health-wellness',
      name: 'Health & Wellness',
      icon: 'HeartPulse',
      color: '#F06F6B',
      gradient: 'linear-gradient(135deg, #F06F6B 0%, #E0528A 100%)',
      subcategories: [
        { id: 'health-insurance', name: 'Health Insurance', icon: 'Stethoscope' },
        { id: 'dental-care', name: 'Dental Care', icon: 'Smile' },
        { id: 'vision-care', name: 'Vision Care', icon: 'Eye' },
        { id: 'mental-health', name: 'Mental Health Services', icon: 'Brain' },
        { id: 'physical-therapy', name: 'Physical Therapy', icon: 'PersonStanding' },
        { id: 'gym-fitness', name: 'Gym & Fitness Memberships', icon: 'Dumbbell' },
        { id: 'personal-training', name: 'Personal Training', icon: 'Zap' },
        { id: 'nutrition-dietitian', name: 'Nutrition & Dietitian Services', icon: 'Apple' },
        { id: 'chiropractic', name: 'Chiropractic Care', icon: 'Bone' },
        { id: 'medical-equipment', name: 'Medical Equipment', icon: 'Stethoscope' },
        { id: 'cosmetic-procedures', name: 'Cosmetic Procedures', icon: 'Sparkles' },
        { id: 'weight-loss-programs', name: 'Weight Loss Programs', icon: 'Scale' },
        { id: 'other-health-wellness', name: 'Other', icon: 'MoreHorizontal' }
      ]
    },
    {
      id: 'accessories',
      name: 'Accessories',
      icon: 'Gem',
      color: '#E0528A',
      gradient: 'linear-gradient(135deg, #E0528A 0%, #F58A54 100%)',
      subcategories: [
        { id: 'sunglasses', name: 'Sunglasses', icon: 'Sun' },
        { id: 'watches', name: 'Watches', icon: 'Watch' },
        { id: 'belts', name: 'Belts', icon: 'Minus' },
        { id: 'bags', name: 'Bags', icon: 'ShoppingBag' },
        { id: 'wallets', name: 'Wallets', icon: 'Wallet' },
        { id: 'jewelry-rings', name: 'Jewelry — Rings', icon: 'Gem' },
        { id: 'jewelry-earrings', name: 'Jewelry — Earrings', icon: 'Gem' },
        { id: 'jewelry-bracelets', name: 'Jewelry — Bracelets', icon: 'Gem' },
        { id: 'jewelry-necklaces', name: 'Jewelry — Necklaces', icon: 'Gem' },
        { id: 'jewelry-pendants', name: 'Jewelry — Pendants', icon: 'Gem' },
        { id: 'jewelry-anklets', name: 'Jewelry — Anklets', icon: 'Gem' },
        { id: 'other-accessories', name: 'Other', icon: 'MoreHorizontal' }
      ]
    }
  ];

  const allOptions = useMemo(() => {
    const options = [];
    categories?.forEach(category => {
      category?.subcategories?.forEach(subcategory => {
        options?.push({
          value: subcategory?.id,
          label: subcategory?.name,
          category: category?.name,
          categoryId: category?.id
        });
      });
    });
    return options;
  }, []);

  const selectedOption = allOptions?.find(option => option?.value === value);
  const selectedCategoryData = categories?.find(cat => cat?.id === selectedCategory);

  const filteredSubcategories = useMemo(() => {
    if (!selectedCategory) return [];
    const cat = categories?.find(c => c?.id === selectedCategory);
    if (!cat) return [];
    if (!searchTerm) return cat?.subcategories;
    return cat?.subcategories?.filter(s =>
      s?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
  }, [selectedCategory, searchTerm]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;
    return categories?.filter(cat =>
      cat?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      cat?.subcategories?.some(s => s?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
    );
  }, [searchTerm]);

  // When searching, collect all matching subcategories across all categories
  const searchMatchedSubcategories = useMemo(() => {
    if (!searchTerm) return [];
    const results = [];
    categories?.forEach(cat => {
      cat?.subcategories?.forEach(sub => {
        if (sub?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())) {
          results?.push({ ...sub, categoryName: cat?.name, categoryData: cat });
        }
      });
    });
    return results;
  }, [searchTerm]);

  // True if search term matches any subcategory names (not just category names)
  const searchMatchesSubcategories = searchMatchedSubcategories?.length > 0;

  const handleOpen = () => {
    setIsOpen(true);
    setSearchTerm('');
    setSelectedCategory(null);
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedCategory(null);
    setSearchTerm('');
    document.body.style.overflow = '';
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat?.id);
    setSearchTerm('');
  };

  const handleSubcategorySelect = (sub) => {
    onChange?.(sub?.id);
    if (onOptionSelect) {
      const option = allOptions?.find(o => o?.value === sub?.id);
      onOptionSelect?.(option || { value: sub?.id, label: sub?.name, category: '' });
    }
    handleClose();
  };

  const handleAIComplete = (result) => {
    if (result?.purchaseType) {
      onChange?.(result?.purchaseType);
    }
    setShowAIAssessment(false);
  };

  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="relative">
      {label && (
        <label className={cn(
          'text-sm font-semibold leading-none mb-2 block',
          error ? 'text-destructive' : 'text-foreground'
        )}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          'w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border-2 transition-all duration-200 text-left',
          value
            ? 'border-primary/40 bg-primary/5 hover:border-primary/60' :'border-border bg-card hover:border-primary/40',
          error && 'border-destructive'
        )}
      >
        <div className="flex items-center gap-3">
          {value ? (
            <>
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: categories?.find(c => c?.subcategories?.some(s => s?.id === value))?.gradient || 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
              >
                <Icon
                  name={categories?.find(c => c?.subcategories?.some(s => s?.id === value))?.icon || 'Package'}
                  size={18}
                  color="white"
                />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground">{selectedOption?.label}</div>
                <div className="text-xs text-text-secondary">{selectedOption?.category}</div>
              </div>
            </>
          ) : (
            <>
              <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <Icon name="Grid3X3" size={18} color="#9CA3AF" />
              </div>
              <span className="text-text-secondary text-sm">{placeholder}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {value && (
            <button
              type="button"
              onClick={(e) => { e?.stopPropagation(); onChange?.(''); }}
              className="w-5 h-5 rounded-full bg-muted flex items-center justify-center hover:bg-border transition-colors"
            >
              <X size={10} />
            </button>
          )}
          <Icon name="ChevronDown" size={16} color="#9CA3AF" />
        </div>
      </button>
      {error && <p className="text-sm text-destructive mt-1.5">{error}</p>}
      {description && !error && <p className="text-sm text-text-secondary mt-1.5">{description}</p>}
      {/* Full-Screen Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) handleClose(); }}
        >
          <div
            className="relative w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col"
            style={{ background: 'var(--background)' }}
          >
            {/* Header */}
            <div
              className="relative px-6 py-5 flex-shrink-0"
              style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/5 blur-2xl" />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-white/5 blur-xl" />
              </div>
              <div className="relative flex items-center justify-between">
                <div>
                  {selectedCategory ? (
                    <button
                      onClick={() => { setSelectedCategory(null); setSearchTerm(''); }}
                      className="flex items-center gap-2 text-white/80 hover:text-white mb-1 transition-colors text-sm"
                    >
                      <ChevronLeft size={16} />
                      Back to stacks
                    </button>
                  ) : null}
                  <h2 className="text-xl font-bold text-white">
                    {selectedCategory ? selectedCategoryData?.name : 'What are you shopping for?'}
                  </h2>
                  <p className="text-white/70 text-sm mt-0.5">
                    {selectedCategory ? 'Choose a subcategory' : 'Pick a stack type to get started'}
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
                >
                  <X size={18} color="white" />
                </button>
              </div>

              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" size={16} />
                <input
                  type="text"
                  placeholder={selectedCategory && !searchMatchesSubcategories ? `Search ${selectedCategoryData?.name}...` : 'Search all stacks...'}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e?.target?.value); if (selectedCategory) setSelectedCategory(null); }}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/15 border border-white/20 text-white placeholder-white/50 text-sm focus:outline-none focus:bg-white/20 focus:border-white/40 transition-all"
                />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {searchMatchesSubcategories ? (
                /* Direct subcategory results when search matches subcategory names */
                (<div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {searchMatchedSubcategories?.map((sub) => (
                    <button
                      key={sub?.id}
                      onClick={() => handleSubcategorySelect(sub)}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                        value === sub?.id
                          ? 'border-primary/50 bg-primary/8' :'border-border bg-card hover:border-primary/30'
                      }`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: sub?.categoryData?.gradient }}
                      >
                        <Icon name={sub?.icon || sub?.categoryData?.icon} size={16} color="white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">{sub?.name}</div>
                        <div className="text-xs text-text-secondary">{sub?.categoryName}</div>
                      </div>
                      {value === sub?.id && (
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: sub?.categoryData?.color }}
                        >
                          <Check size={11} color="white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>)
              ) : !selectedCategory ? (
                /* Category Grid */
                (<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {filteredCategories?.map((cat) => (
                    <button
                      key={cat?.id}
                      onClick={() => handleCategoryClick(cat)}
                      className="group flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2 border-border hover:border-transparent transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 bg-card"
                    >
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200"
                        style={{ background: cat?.gradient }}
                      >
                        <Icon name={cat?.icon} size={26} color="white" />
                      </div>
                      <span className="text-xs font-semibold text-foreground text-center leading-tight">{cat?.name}</span>
                      <span className="text-xs text-text-secondary">{cat?.subcategories?.length} options</span>
                    </button>
                  ))}
                </div>)
              ) : (
                /* Subcategory List */
                (<div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {filteredSubcategories?.map((sub) => (
                    <button
                      key={sub?.id}
                      onClick={() => handleSubcategorySelect(sub)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5',
                        value === sub?.id
                          ? 'border-primary/50 bg-primary/8' :'border-border bg-card hover:border-primary/30'
                      )}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: selectedCategoryData?.gradient }}
                      >
                        <Icon name={sub?.icon || selectedCategoryData?.icon} size={16} color="white" />
                      </div>
                      <span className="text-sm font-medium text-foreground flex-1">{sub?.name}</span>
                      {value === sub?.id && (
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: selectedCategoryData?.color }}
                        >
                          <Check size={11} color="white" />
                        </div>
                      )}
                    </button>
                  ))}
                  {filteredSubcategories?.length === 0 && (
                    <div className="col-span-2 text-center py-8 text-text-secondary text-sm">
                      No options found for "{searchTerm}"
                    </div>
                  )}
                </div>)
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-border flex-shrink-0 flex items-center justify-between bg-muted/30">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-xl text-sm font-medium text-text-secondary hover:text-foreground hover:bg-muted transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* AI Assessment Modal */}
      {showAIAssessment && (
        <AIAssessmentChat
          onComplete={handleAIComplete}
          onClose={() => setShowAIAssessment(false)}
          persistedMessages={[]}
          persistedUserResponses={{}}
          persistedConversationState={null}
          onStateChange={() => {}}
          onAddSingleOption={() => {}}
        />
      )}
    </div>
  );
};

export default CategorySelect;